import { notificationsKeys } from "@/modules/notifications/presentation/api/queries";
import type {
  LedgerListParams,
  RefundQueueListParams,
  TopupQueueListParams,
  WalletAccountsListParams,
} from "./client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { walletApi } from "./client";

export const walletKeys = {
  summary: () => ["wallet", "summary"] as const,
  accountsList: (params: WalletAccountsListParams) =>
    ["wallet", "accounts", params] as const,
  topupQueueSummary: () => ["wallet", "topup-queue", "summary"] as const,
  topupQueueList: (params: TopupQueueListParams) =>
    ["wallet", "topup-queue", params] as const,
  refundQueueSummary: () => ["wallet", "refund-queue", "summary"] as const,
  refundQueueList: (params: RefundQueueListParams) =>
    ["wallet", "refund-queue", params] as const,
  ledgerSummary: (params: Omit<LedgerListParams, "limit" | "offset" | "sortField" | "sortOrder">) =>
    ["wallet", "ledger", "summary", params] as const,
  ledgerList: (params: LedgerListParams) =>
    ["wallet", "ledger", params] as const,
};

export function useWalletSummaryQuery() {
  return useQuery({
    queryKey: walletKeys.summary(),
    queryFn: () => walletApi.summary(),
  });
}

export function useWalletAccountsQuery(params: WalletAccountsListParams = {}) {
  return useQuery({
    queryKey: walletKeys.accountsList(params),
    queryFn: () => walletApi.listAccounts(params),
  });
}

export function useTopupQueueSummaryQuery() {
  return useQuery({
    queryKey: walletKeys.topupQueueSummary(),
    queryFn: () => walletApi.getTopupQueueSummary(),
  });
}

export function useTopupQueueQuery(params: TopupQueueListParams = {}) {
  return useQuery({
    queryKey: walletKeys.topupQueueList(params),
    queryFn: () => walletApi.listTopupQueue(params),
  });
}

export function useApproveTopupMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; totpCode?: string }) =>
      walletApi.approveTopup(input.id, input.totpCode, crypto.randomUUID()),
    onSuccess: (_, __) => {
      qc.invalidateQueries({ queryKey: ["wallet", "topup-queue"] });
      qc.invalidateQueries({ queryKey: ["wallet", "summary"] });
      qc.invalidateQueries({ queryKey: notificationsKeys.summary() });
    },
  });
}

export function useRejectTopupMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => walletApi.rejectTopup(id, crypto.randomUUID()),
    onSuccess: (_, __) => {
      qc.invalidateQueries({ queryKey: ["wallet", "topup-queue"] });
      qc.invalidateQueries({ queryKey: ["wallet", "summary"] });
      qc.invalidateQueries({ queryKey: notificationsKeys.summary() });
    },
  });
}

export function useRefundQueueSummaryQuery() {
  return useQuery({
    queryKey: walletKeys.refundQueueSummary(),
    queryFn: () => walletApi.getRefundQueueSummary(),
  });
}

export function useRefundQueueQuery(params: RefundQueueListParams = {}) {
  return useQuery({
    queryKey: walletKeys.refundQueueList(params),
    queryFn: () => walletApi.listRefundQueue(params),
  });
}

export function useApproveRefundMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; totpCode?: string }) =>
      walletApi.approveRefund(input.id, input.totpCode),
    onSuccess: (_, __) => {
      qc.invalidateQueries({ queryKey: ["wallet", "refund-queue"] });
      qc.invalidateQueries({ queryKey: ["wallet", "summary"] });
      qc.invalidateQueries({ queryKey: notificationsKeys.summary() });
    },
  });
}

export function useRejectRefundMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => walletApi.rejectRefund(id),
    onSuccess: (_, __) => {
      qc.invalidateQueries({ queryKey: ["wallet", "refund-queue"] });
      qc.invalidateQueries({ queryKey: ["wallet", "summary"] });
      qc.invalidateQueries({ queryKey: notificationsKeys.summary() });
    },
  });
}

export function useLedgerSummaryQuery(
  params: Omit<LedgerListParams, "limit" | "offset" | "sortField" | "sortOrder"> = {},
) {
  return useQuery({
    queryKey: walletKeys.ledgerSummary(params),
    queryFn: () => walletApi.getLedgerSummary(params),
  });
}

export function useLedgerQuery(params: LedgerListParams = {}) {
  return useQuery({
    queryKey: walletKeys.ledgerList(params),
    queryFn: () => walletApi.listLedger(params),
  });
}
